import { ITicket, ITicketFormData } from '@/interfaces/tickets/ITicket';
import { apiService } from './api.service';
import { ISingleResponse, IListResponse } from '@/interfaces/api/IApiBaseResponse';
import { IBulkTicket } from '@/interfaces/tickets/IBulkTicket';

class TicketsService {
  async getAllTickets(page?: number, size?: number, fields?: string[]): Promise<IListResponse<ITicket>> {
    const params = new URLSearchParams();
    if (page) params.append('page[number]', page.toString());
    if (size) params.append('page[size]', size.toString());
    if (fields?.length) params.append('fields', fields.join(','));

    return await apiService.get<IListResponse<ITicket>>(`/tickets?${params.toString()}`);
  }

  async getMyTickets(options?: {
    page?: number;
    size?: number;
    fields?: string[];
    sort?: { [key: string]: 'ASC' | 'DESC' };
  }): Promise<IListResponse<ITicket>> {
    const params = new URLSearchParams();
    
    if (options?.page) params.append('page[number]', options.page.toString());
    if (options?.size) params.append('page[size]', options.size.toString());
    if (options?.fields?.length) params.append('fields', options.fields.join(','));
    if (options?.sort) {
      Object.entries(options.sort).forEach(([key, value]) => {
        params.append(`sort[${key}]`, value);
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/tickets/my-tickets?${queryString}` : '/tickets/my-tickets';
    return await apiService.get<IListResponse<ITicket>>(url);
  }

  async getTicketById(uuid: string): Promise<ISingleResponse<ITicket>> {
    return await apiService.get<ISingleResponse<ITicket>>(`/tickets/${uuid}`);
  }

  async downloadTicket(uuid: string): Promise<Blob> {
    const response = await apiService.get(`/tickets/${uuid}/download`, {
      responseType: 'blob'
    }) as Blob;
    return response;
  }

  async createTicket(ticketData: ITicketFormData): Promise<ISingleResponse<ITicket>> {
    const formData = new FormData();
    formData.append('file', ticketData.file[0]);
    formData.append('eventName', ticketData.eventName);
    formData.append('payment_status', ticketData.payment_status);
    formData.append('email', ticketData.email);
    formData.append('emmiter_note', ticketData.emmiter_note);
    formData.append('recipient_note', ticketData.recipient_note);
    formData.append('price', ticketData.price.toString());

    return await apiService.post<ISingleResponse<ITicket>>('/tickets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  async updateTicket(uuid: string, ticketData: Partial<ITicketFormData>): Promise<ISingleResponse<ITicket>> {
    const formData = new FormData();
    if (ticketData.file) formData.append('file', ticketData.file[0]);
    if (ticketData.eventName) formData.append('eventName', ticketData.eventName);
    if (ticketData.payment_status) formData.append('payment_status', ticketData.payment_status);
    if (ticketData.email) formData.append('email', ticketData.email);
    if (ticketData.emmiter_note) formData.append('emmiter_note', ticketData.emmiter_note);
    if (ticketData.recipient_note) formData.append('recipient_note', ticketData.recipient_note);
    if (ticketData.price) formData.append('price', ticketData.price.toString());

    return await apiService.put<ISingleResponse<ITicket>>(`/tickets/${uuid}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  async deleteTicket(uuid: string): Promise<void> {
    await apiService.delete(`/tickets/${uuid}`);
  }

  async sendReminder(uuid: string): Promise<void> {
    await apiService.post(`/tickets/${uuid}/send-reminder`, {});
  }

  async createBulkTickets(formData: FormData): Promise<IListResponse<IBulkTicket>> {
    return await apiService.post<IListResponse<IBulkTicket>>('/tickets/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  async updateRecipientNote(uuid: string, recipient_note: string): Promise<ISingleResponse<ITicket>> {
    return await apiService.patch<ISingleResponse<ITicket>>(`/tickets/${uuid}/recipient-note`, { recipient_note });
  }
}

export const ticketsService = new TicketsService();